import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import moment from 'moment';
import { Calendar, X, Loader2, List, ListX } from 'lucide-react';
import { Calendar as CalendarComponent } from './Calendar';
import { EventsList } from './EventsList';
import { EventTypeFilter } from './EventTypeFilter';
import UserService from '../../../../store/services/user.service';
import './DateSelector.scss';

export const DateSelector = ({ onDateRangeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [events, setEvents] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingNew, setIsFetchingNew] = useState(false);
  const [showEvents, setShowEvents] = useState(true);
  const [selectedEventTypes, setSelectedEventTypes] = useState(() => {
    const stored = localStorage.getItem('eventSummaryFilter');
    return stored ? JSON.parse(stored) : [2, 3, 4, 5]; // All types selected by default
  });
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);
  const dragHandleRef = useRef(null);

  // Get current work week (Monday to Friday starting from today)
  const getCurrentWorkWeek = useCallback(() => {
    const today = moment();
    const startOfWeek = today.clone().startOf('isoWeek'); // Monday
    const endOfWeek = today.clone().endOf('isoWeek').subtract(2, 'days'); // Friday
    
    // If today is before Friday, start from today, otherwise start from Monday
    const start = today.isBefore(endOfWeek) ? today : startOfWeek;
    
    return {
      start: start.clone(),
      end: endOfWeek.clone()
    };
  }, []);

  const initializeDateRange = useCallback(() => {
    const workWeek = getCurrentWorkWeek();
    setSelectedStartDate(workWeek.start);
    setSelectedEndDate(workWeek.end);
    return workWeek;
  }, [getCurrentWorkWeek]);

  const fetchEvents = useCallback(async (startDate, endDate, isNewFetch = false, forceNewFetch = false) => {
    if (isNewFetch) {
      setIsFetchingNew(true);
    } else {
      setIsLoading(true);
    }

    try {
      if (onDateRangeChange) {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const startDateParam =  startDate.startOf('day').clone();
        const endDataParam =  endDate.endOf('day').clone();
        const params = {
          tz: timezone,
          startDate: startDateParam.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
          endDate: endDataParam.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
        };
        
        let response;
        var storedEvents = localStorage.getItem("eventSummary");
        if(!forceNewFetch && storedEvents)
          response = JSON.parse(storedEvents);
        else{
          response = await UserService.getUserEvents(params);
          localStorage.setItem("eventSummary", JSON.stringify(response));
        }
        setEvents(response);
        onDateRangeChange(params.startDate, params.endDate);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setIsLoading(false);
      setIsFetchingNew(false);
    }
  }, [onDateRangeChange]);

  const currentDateRange = useMemo(() => {
    if (!selectedStartDate || !selectedEndDate) {
      return initializeDateRange();
    }
    return {
      start: selectedStartDate,
      end: selectedEndDate
    };
  }, [selectedStartDate, selectedEndDate, initializeDateRange]);

  const handleTogglePopover = useCallback(() => {
    if (!isOpen) {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPopoverPosition({
          top: rect.bottom + 8,
          left: rect.left
        });
      }
      setIsOpen(true);
      if (currentDateRange.start && currentDateRange.end) {
        fetchEvents(currentDateRange.start, currentDateRange.end, !!events, true);
      }
    }
    else{
      setIsOpen(false);
    }
  }, [isOpen, currentDateRange, fetchEvents, events]);

  const handleDateRangeChange = useCallback((start, end) => {
    setSelectedStartDate(start);
    setSelectedEndDate(end);
    fetchEvents(start, end, true, true);
  }, [fetchEvents]);

  const handleClosePopover = useCallback(() => {
    setIsOpen(false);
    setIsDragging(false);
  }, []);

  const handleDragStart = useCallback((e) => {
    if ((e.target === dragHandleRef.current || dragHandleRef.current?.contains(e.target)) && 
        !e.target.closest('button')) {
      setIsDragging(true);
      const rect = popoverRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  }, []);

  const handleDragMove = useCallback((e) => {
    if (isDragging && popoverRef.current) {
      e.preventDefault();
      setPopoverPosition({
        top: e.clientY - dragOffset.y,
        left: e.clientX - dragOffset.x
      });
    }
  }, [isDragging, dragOffset]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  useEffect(() => {
    const workWeek = initializeDateRange();
    fetchEvents(workWeek.start, workWeek.end);
  }, [initializeDateRange, fetchEvents]);

  const displayDate = useMemo(() => {
    const today = moment();
    return today.format('dddd, MMMM D, YYYY');
  }, []);

  const handleFilterChange = useCallback((newSelectedTypes) => {
    setSelectedEventTypes(newSelectedTypes);
    localStorage.setItem('eventSummaryFilter', JSON.stringify(newSelectedTypes));
  }, []);

  const filteredEvents = useMemo(() => {
    if (!events?.events) return [];
    return events.events.filter(event => selectedEventTypes.includes(event.type));
  }, [events, selectedEventTypes]);

  const totalEventsCount = useMemo(() => {
    if (!events?.events) return 0;
    
    return filteredEvents.filter(eventData => {
      const event = eventData.event;
      let eventDate;
      
      if (eventData.type === 2) {
        // Task - check if any part of the task overlaps with selected range
        const taskStart = event.startDate ? moment(event.startDate) : null;
        const taskEnd = event.endDate ? moment(event.endDate) : null;
        
        if (!taskStart && !taskEnd) return false;
        
        // Only start date - check if start is within range
        if (taskStart && !taskEnd) {
          return selectedStartDate && selectedEndDate && 
                 taskStart.isBetween(selectedStartDate, selectedEndDate, 'day', '[]');
        }
        
        // Only end date - check if end is within range
        if (!taskStart && taskEnd) {
          return selectedStartDate && selectedEndDate && 
                 taskEnd.isBetween(selectedStartDate, selectedEndDate, 'day', '[]');
        }
        
        // Both dates - check if any part overlaps
        return selectedStartDate && selectedEndDate && 
               (taskStart.isBetween(selectedStartDate, selectedEndDate, 'day', '[]') ||
                taskEnd.isBetween(selectedStartDate, selectedEndDate, 'day', '[]') ||
                (taskStart.isSameOrBefore(selectedStartDate) && taskEnd.isSameOrAfter(selectedEndDate)));
      } else if (eventData.type === 3 && event.dateImpact) {
        // Risk - use impact date
        eventDate = moment(event.dateImpact);
      } else if (eventData.type === 4 && event.startTime) {
        // Calendar Event - use start time
        eventDate = moment(event.startTime);
      } else if (eventData.type === 5 && event.startTime) {
        // Communication Event - use start time
        eventDate = moment(event.startTime);
      } else {
        return false;
      }
      
      if (eventDate) {
        return selectedStartDate && selectedEndDate && 
               eventDate.isBetween(selectedStartDate, selectedEndDate, 'day', '[]');
      }
      
      return false;
    }).length;
  }, [filteredEvents, selectedStartDate, selectedEndDate]);

  return (
    <div className="date-selector-component">
      <div className="date-selector">
      <button 
        ref={triggerRef}
        className="date-selector__trigger"
        onClick={handleTogglePopover}
        disabled={isLoading && !events}
      >
        <Calendar className="date-selector__icon" />
        <span className="date-selector__text">
          {displayDate}
          {totalEventsCount > 0 && (
            <span className="date-selector__event-badge">
              {totalEventsCount}
            </span>
          )}
        </span>
        {isFetchingNew && (
          <Loader2 className="date-selector__loading-icon" />
        )}
      </button>

      {isOpen && (
        <div 
          ref={popoverRef}
          className={`date-selector__popover ${isDragging ? 'date-selector__popover--dragging' : ''}`}
          style={{
            position: 'fixed',
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`,
            zIndex: 1000
          }}
        >
          <div 
            ref={dragHandleRef}
            className="date-selector__header"
            onMouseDown={handleDragStart}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
              <h3 className="date-selector__title">Event Summary</h3>
              <div className="date-selector__controls">
                <button
                  className="date-selector__toggle-events"
                  onClick={() => setShowEvents(!showEvents)}
                  title={showEvents ? "Hide events list" : "Show events list"}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {showEvents ? <List /> : <ListX />}
                </button>
                <button
                  className="date-selector__close"
                  onClick={handleClosePopover}
                  title="Close"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <X />
                </button>
              </div>
            </div>

            <div className="date-selector__content">
              <CalendarComponent
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
                onDateRangeChange={handleDateRangeChange}
                events={filteredEvents}
              />

              {showEvents && (
                <>
                  {!isLoading && !isFetchingNew &&
                    <EventTypeFilter
                    events={events?.events || []}
                    selectedTypes={selectedEventTypes}
                    onFilterChange={handleFilterChange}
                  />
                  }
                  <EventsList
                    events={filteredEvents}
                    isLoading={isLoading}
                    isFetchingNew={isFetchingNew}
                    dateRange={{
                      start: currentDateRange.start,
                      end: currentDateRange.end
                    }}
                  />
                </>
              )}
            </div>
          </div>
      )}
    </div>
    </div>
  );
};